import proj4 from 'proj4';

// Coordinate systems a Montenegrin surveyor is likely to type in. Locations are always
// stored as WGS84 latitude/longitude; the local systems are converted in the browser.
//
// Gauss-Krüger zone 6 (MGI 1901 / Balkans zone 6, EPSG:3908) uses the published 3-parameter
// datum shift, good to a few metres. That is enough to place a job on the map, not for survey work.
proj4.defs('GK6', '+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs');
proj4.defs('UTM34', '+proj=utm +zone=34 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');

export const CRS = {
    WGS84: { key: 'WGS84', first: 'lat', second: 'lng', decimals: 6 },
    GK6: { key: 'GK6', first: 'Y', second: 'X', decimals: 2 },
    UTM34: { key: 'UTM34', first: 'E', second: 'N', decimals: 2 },
};
export const CRS_ORDER = ['WGS84', 'GK6', 'UTM34'];

/** Default view: Montenegro. */
export const DEFAULT_CENTER = [42.75, 19.25];
export const DEFAULT_ZOOM = 8;

const num = (v) => {
    if (v === null || v === undefined) return NaN;
    return Number(String(v).trim().replace(/\s/g, '').replace(',', '.'));
};

const inMontenegroArea = (lat, lng) => lat > 40.5 && lat < 44 && lng > 17 && lng < 21.5;

/**
 * Converts the two typed values in `crs` to { lat, lng }.
 * GK6 is entered as Y (easting, 6…) and X (northing, 4…), UTM34 as E and N.
 * Returns { error } with an i18n key when the input is not usable.
 */
export function toLatLng(crs, a, b) {
    const x = num(a), y = num(b);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return { error: 'location.errors.number' };
    let lat, lng;
    if (crs === 'WGS84') {
        lat = x; lng = y;
        if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return { error: 'location.errors.range' };
    } else if (crs === 'GK6') {
        [lng, lat] = proj4('GK6', 'WGS84', [x, y]);
    } else {
        [lng, lat] = proj4('UTM34', 'WGS84', [x, y]);
    }
    if (crs !== 'WGS84' && !inMontenegroArea(lat, lng)) return { error: 'location.errors.outside' };
    return { lat: round(lat, 7), lng: round(lng, 7) };
}

/** WGS84 point as the two display values of `crs`. */
export function fromLatLng(crs, lat, lng) {
    if (crs === 'WGS84') return [lat.toFixed(6), lng.toFixed(6)];
    const [e, n] = proj4('WGS84', crs, [lng, lat]);
    return [e.toFixed(2), n.toFixed(2)];
}

/** Accepts "41.92968, 19.21562" or "41.92968 19.21562" pasted into one field. */
export function splitPair(text) {
    const m = String(text).trim().match(/^(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)$/);
    return m ? [m[1], m[2]] : null;
}

/** Guesses the system from a pasted pair, so surveyors can paste instrument output directly. */
export function guessCrs(a, b) {
    const x = num(a), y = num(b);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    if (Math.abs(x) <= 90 && Math.abs(y) <= 180) return 'WGS84';
    if (x > 6_200_000 && x < 6_900_000 && y > 4_400_000 && y < 5_100_000) return 'GK6';
    if (x > 100_000 && x < 900_000 && y > 4_400_000 && y < 5_100_000) return 'UTM34';
    return null;
}

const round = (v, d) => Math.round(v * 10 ** d) / 10 ** d;

/**
 * Link that opens eMapa (emapa.me) at this point; eMapa then shows the parcel info there.
 * Uses eMapa's own share-link format (base64url of "19269|lat*1e6|lng*1e6|zoom"). It is not an
 * official API: if eMapa changes the format the link still opens eMapa, just not at the point.
 */
export function emapaUrl(lat, lng, zoom = 18) {
    const raw = `19269|${Math.round(lat * 1e6)}|${Math.round(lng * 1e6)}|${zoom}`;
    const b64 = btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `https://emapa.me/?s=${b64}`;
}

/**
 * Directions to this point in Google Maps (free, no API key). No origin: Maps starts from where the phone is, which
 * suits crews leaving from home, another site or the cadastre. On phones it opens the Maps app when installed.
 */
export const navigationUrl = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

export const fmtDistance = (m) => (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`);
